import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config as sanitizeConfig } from 'sanitize-text/sanitizers/SanitizeConfiguration';

// Copied from https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/setup/methods.js#L10-L32
const htmlEscapeMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
};

const htmlCharPattern = '(?:&|<|>|"|\'|`)';
const htmlTestRegexp = RegExp(htmlCharPattern);
const htmlReplaceRegexp = RegExp(htmlCharPattern, 'g');

const htmlReplacement = (match: string) =>
  htmlEscapeMap[match as keyof typeof htmlEscapeMap];

const htmlEscape = (value: string) => {
  if (htmlTestRegexp.test(value)) {
    return value.replace(htmlReplaceRegexp, htmlReplacement);
  }
  return value;
};

// Inspired by https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/setup/methods.js#L819
export const formatParam = (
  optName: string,
  optValue: string | number | ReturnType<typeof I18n.SafeString>
) => {
  if (typeof optValue === 'number') {
    if (isNaN(optValue)) {
      return null;
    }
    if (optName === 'count') {
      return Number(optValue);
    }
    return I18n.formatNumber(optValue);
  } else if (optValue instanceof I18n.SafeString) {
    return optValue.toString();
  } else if (optValue != null) {
    return htmlEscape(String(optValue));
  }
  return optValue;
};

const placeholderRegex = /(?:\{\{)\s?(\S*?)\s?(?:\}\})/gm;

// Inspired by https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/i18n.js#L495
export const interpolate = (
  message: string,
  options: Record<string, string | number>
) => {
  const modifiedOptions = {} as Record<string, string | number | null>;
  for (const optName in options) {
    if (Object.prototype.hasOwnProperty.call(options, optName)) {
      const optValue = options[optName];
      if (optValue != null) {
        modifiedOptions[optName] = formatParam(optName, optValue);
      }
    }
  }
  const matches = message.match(placeholderRegex);
  if (!matches) {
    return message;
  }
  let remainingMessage = message;
  let placeholder;
  let name;
  let regex;
  let value;
  while (matches.length) {
    placeholder = matches.shift() as string;
    name = placeholder.replace(placeholderRegex, '$1');
    if (modifiedOptions[name] != null) {
      value = modifiedOptions[name]!.toString().replace(/\$/gm, '_#$#_');
    } else {
      // TODO report missing placeholder
      value = '';
    }
    regex = new RegExp(
      placeholder.replace(/\{/gm, '\\{').replace(/\}/gm, '\\}')
    );
    remainingMessage = remainingMessage.replace(regex, value);
  }
  return remainingMessage.replace(/_#\$#_/g, '$');
};

export const validText = (
  message: string,
  options: Record<string, string | number>,
  defaultValue = '',
  unescapeText = false
) => {
  if (!isValidI18nKey(message)) {
    return sanitize(
      interpolate(defaultValue, options),
      sanitizeConfig.TEXTONLY
    );
  }
  return unescapeText
    ? unescapedText(message, options)
    : I18n.text(message, options);
};

export const validUnescapedText = (
  ...args: [string, Record<string, string | number>, string]
) => validText(...args, true);
